import {DateTime, Interval} from "luxon";
import {DateHelper} from "../helpers/date.helper";
import {CalendarDay} from "./calendar-day.interface";
import {CalendarMonth} from "./calendar-month.interface";
import {CalendarWeek} from "./calendar-week.interface";
import {CalendarWeekday} from "./calendar-weekday.interface";

export class CalendarViewCalculator {

    private _currentDateTime: DateTime;
    private _interval: Interval;
    private readonly _pointer: DateTime;

    constructor(interval: Interval, pointer: DateTime) {
        this._pointer = pointer;
        this._interval = interval;
        this._currentDateTime = this._interval.start!;
    }

    private _days: CalendarDay[] = [];

    public get days(): CalendarDay[] {
        if (this._days.length < 1) {

            for (let i = 0; i < this.numberOfDays; i++) {
                const date = this._interval.start!.plus({day: i});

                this._days.push({
                    id: i,
                    dayOfMonth: date.day,
                    weekDay: date.weekdayShort ?? ``,
                    isWeekEnd: [6, 7].includes(date.weekday),
                    dateTime: date,
                    isToday: DateHelper.today.equals(date),
                    isFocusedDay: date.equals(this._pointer)
                });
            }
        }

        return this._days;
    }

    private _months: CalendarMonth[] = [];

    public get months(): CalendarMonth[] {
        if (this._months.length < 1) {
            const numberOfMonths = this._interval.count(`months`);
            let numberOfDays = this.numberOfDays;

            for (let i = 0; i < numberOfMonths; i++) {
                let monthStart = this._interval.start!.plus({month: i});
                if (i !== 0) {
                    monthStart = monthStart.startOf(`month`);
                }

                let monthEnd = monthStart.endOf(`month`);
                if (this._interval.isAfter(monthEnd)) {
                    monthEnd = this._interval.end!;
                }

                let daysInMonth = Interval.fromDateTimes(monthStart, monthEnd).count(`days`);

                if (daysInMonth > numberOfDays) {
                    daysInMonth = numberOfDays;
                }

                const start = Interval.fromDateTimes(this._interval.start!, monthStart).count(`days`)
                this._months.push({
                    id: monthStart.month,
                    name: monthStart.monthShort!,
                    year: monthStart.year,
                    count: daysInMonth,
                    index: {
                        start,
                        end: start + daysInMonth
                    }
                });

                numberOfDays -= daysInMonth;
            }
        }

        return this._months;
    }

    private _weekDays: CalendarWeekday[] = [];

    public get weekDays(): CalendarWeekday[] {
        if (this._weekDays.length < 1) {

            for (let i = 0; i < this.numberOfDays; i++) {
                const day = this._interval.start!.plus({day: i});

                this._weekDays.push({
                    id: i,
                    name: day.weekdayShort!,
                    isWeekend: [6, 7].includes(day.weekday),
                    isToday: DateHelper.today.equals(day),
                    isFocusedDay: day.equals(this._pointer)
                });
            }
        }

        return this._weekDays;
    }

    private _weeks: CalendarWeek[] = [];

    public get weeks(): CalendarWeek[] {
        if (this._weeks.length < 1) {

            const numberOfWeeks = this._interval.count(`weeks`);
            let numberOfDays = this.numberOfDays;

            for (let i = 0; i < numberOfWeeks; i++) {
                let weekStart = this._interval.start!.plus({week: i});
                if (i !== 0) {
                    weekStart = weekStart.startOf(`week`);
                }

                let weekEnd = weekStart.endOf(`week`);
                if (this._interval.isAfter(weekEnd)) {
                    weekEnd = this._interval.end!;
                }

                let daysInWeek = Math.round(weekEnd.diff(weekStart, 'days').days);
                if (daysInWeek > numberOfDays) {
                    daysInWeek = numberOfDays;
                }

                const start = Interval.fromDateTimes(this._interval.start!, weekStart).count(`days`)
                this._weeks.push({
                    id: weekStart.weekNumber,
                    num: weekStart.weekNumber,
                    count: daysInWeek,
                    index: {
                        start,
                        end: start + daysInWeek
                    }
                });
                numberOfDays -= daysInWeek;
            }
        }

        return this._weeks;
    }

    private _years: number[] = [];

    public get years(): number[] {
        if (this._years.length < 1) {
            const numberOfYears = this._interval.count(`years`);
            for (let i = 0; i < numberOfYears; i++) {
                this._years.push(this._interval.start!.year + i);
            }
        }

        return this._years;
    }

    public get currentDay(): number {
        return this._currentDateTime.day;
    }

    public get currentWeekday(): string {
        return this._currentDateTime.weekdayShort!;
    }

    public get numberOfDays(): number {
        return this._interval.count(`days`);
    }

    public getDay(i: number): CalendarDay | null {
        return this.days[i] ?? null;
    }

    public next(): DateTime {
        this._currentDateTime = this._currentDateTime.plus({day: 1});

        return this._currentDateTime;
    }
}